import React, {useEffect, useRef, memo, useMemo, useState} from "react";
import {ChatContainer, ChatInput, ChatScroll} from "@unibuddy/chat-ui";
import {Box, View, Text} from "@unibuddy/patron";
import {useAnalytics} from "@unibuddy/tracking";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";

import {AssistantConversationMessage, SenderRole} from "../../../types/generated";
import {ChatMessage} from "./ChatMessage/ChatMessage";
import {
    ConversationState,
    useAssistantConversationContext,
} from "../providers/AssistantConversationProvider";
import {useAssistantInitiationContext} from "../providers/AssistantInitiationProvider";
import {ChatMessageTypingBubble} from "./ChatMessage/ChatMessageTypingBubble";
import {ChatFooter} from "./ChatFooter/ChatFooter";
import {ChatSuggestedQuestions} from "./ChatSuggestedQuestions/ChatSuggestedQuestions";
import {
    PROSPECT_GUIDANCE_MESSAGE_CHARACTER_LIMIT,
    SHOW_SUGGESTED_QUESTIONS,
} from "../../../utils/featureFlags";
import {ConversationPopup} from "./ConversationPopup/ConversationPopup";
import {MINIMUM_MESSAGES_FOR_POPUP} from "./ConversationPopup/utils/constants";
import {useScreensizeContext} from "../../../providers/ScreensizeProvider";
import {ChatMessageCharacterCount} from "./ChatMessage/ChatMessageCharacterCount";
import {ErrorMessage} from "./ErrorMessage/ErrorMessage";
import {MESSAGE_CHARACTER_LIMIT} from "../utils/constants";
import {events} from "../../../analytics/productSpecification";
import {useUniversityContext} from "../../../providers/UniversityProvider";

type MessagesProps = {
    messages: AssistantConversationMessage[];
    showAssistantTyping: boolean;
};
const Messages = memo(({messages, showAssistantTyping}: MessagesProps) => {
    return (
        <>
            {messages.map((message: AssistantConversationMessage, index, array) => {
                return (
                    <ChatMessage key={message.id} message={message} index={index} array={array} />
                );
            })}
            {messages.at(-1)?.sender !== SenderRole.Assistant && showAssistantTyping && (
                <ChatMessageTypingBubble key={messages.length + 1} />
            )}
        </>
    );
});
Messages.displayName = "Messages";

type ScrollMessagesType = Parameters<typeof ChatScroll>[0]["messages"];

export function ChatMessages() {
    const {loading, userHasSentMessage} = useAssistantInitiationContext();
    const {isSmallDeviceDimension, isSmallInnerHeight} = useScreensizeContext();
    const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
    const introButtonRef = useRef<HTMLButtonElement | null>(null);
    const {trackEvent} = useAnalytics();
    const {universitySlug} = useUniversityContext();
    const {onSendMessage, conversationState, messages, error} = useAssistantConversationContext();
    const showSuggestedQuestions = useBooleanFeatureFlag(
        SHOW_SUGGESTED_QUESTIONS.key,
        SHOW_SUGGESTED_QUESTIONS.defaultValue,
    );
    const characterLimitEnabled = useBooleanFeatureFlag(
        PROSPECT_GUIDANCE_MESSAGE_CHARACTER_LIMIT.key,
        PROSPECT_GUIDANCE_MESSAGE_CHARACTER_LIMIT.defaultValue,
    );
    const [input, setInput] = useState("");
    const prevInput = useRef("");
    const charLimitErrorMessage = `Please keep the message within ${MESSAGE_CHARACTER_LIMIT} characters.`;
    const isDisabled = loading || conversationState !== ConversationState.USER_CAN_SEND_MESSAGE;
    const prevDisabled = useRef<boolean>(isDisabled);
    const showConversationPopup = messages.length > MINIMUM_MESSAGES_FOR_POPUP;
    const errorMessageEnabled = input.length > MESSAGE_CHARACTER_LIMIT && characterLimitEnabled;
    const assistantIsTyping = conversationState === ConversationState.ASSISTANT_PROCESSING_RESPONSE;

    const scrollMessages: ScrollMessagesType = useMemo(() => {
        const newMessages = messages.map((message) => {
            return {id: message.id};
        });

        if (assistantIsTyping) {
            newMessages.push({
                id: "typing",
            });
        }

        return newMessages;
    }, [assistantIsTyping, messages]);

    const updateCharCount = (queryReq: string) => {
        setInput((prevState) => {
            prevInput.current = prevState;
            return queryReq;
        });
    };

    useEffect(() => {
        if (prevDisabled.current && !isDisabled) {
            chatInputRef.current?.focus();
        }
        prevDisabled.current = isDisabled;
    }, [isDisabled]);

    useEffect(() => {
        const messageListener = (e: MessageEvent) => {
            if (e.data === "chatVisible=true") {
                // Must be < 2 to account for intro message
                // We handle elsewhere if isSmallDeviceDimension is true
                if (scrollMessages.length < 2 && !isSmallDeviceDimension) {
                    introButtonRef.current?.focus();
                }
            }
        };

        window.addEventListener("message", messageListener, true);

        return () => {
            window.removeEventListener("message", messageListener);
        };
    }, [scrollMessages.length, isSmallDeviceDimension]);

    if (error) {
        return (
            <View h="100%" w="100%" bgColor="grey50">
                <Text>Assistant Error</Text>
            </View>
        );
    }

    return (
        <ChatContainer backgroundColor="grey50">
            {showConversationPopup && <ConversationPopup />}
            <ChatScroll
                blur={false}
                loading={false}
                hasMoreMessages={false}
                messages={scrollMessages}
            >
                <Box marginY="small-medium" role="listitem">
                    <Text size="xsmall" color="grey600" align="center">
                        The Assistant can make mistakes. Verify important information.
                    </Text>
                </Box>

                <Messages messages={messages} showAssistantTyping={assistantIsTyping} />
                {!userHasSentMessage && showSuggestedQuestions && isSmallInnerHeight && (
                    <ChatSuggestedQuestions />
                )}
            </ChatScroll>
            {!userHasSentMessage && showSuggestedQuestions && !isSmallInnerHeight && (
                <ChatSuggestedQuestions />
            )}
            {errorMessageEnabled && (
                <ErrorMessage title="Character limit reached" message={charLimitErrorMessage} />
            )}
            <ChatFooter>
                <ChatInput
                    ref={chatInputRef}
                    value={input}
                    onSend={(e) => {
                        onSendMessage(e).catch(() => updateCharCount(prevInput.current));
                        trackEvent(events.sendMessageClicked.name, {
                            product: "Assistant",
                            cta: "Send Message",
                            universitySlug,
                        });
                    }}
                    disabled={isDisabled}
                    onChangeText={updateCharCount}
                />
                {characterLimitEnabled && <ChatMessageCharacterCount count={input.length} />}
            </ChatFooter>
        </ChatContainer>
    );
}
